import type {
    Invoice,
    StorageModuleCreateResult,
    Vendor,
    VendorSuggestion,
} from '~/storage/types'
import type {
    InvoiceClassificationResult,
    InvoiceDataExtractionResult,
    ParsedInvoiceMetadata,
} from '~common/invoice-processing/types'
import type { ProcessEmailCrudeReturn } from '../services/types'

import { fromPairs, fromPairsMulti } from '~/utils/from-pairs'
import {
    setDoc,
    query,
    where,
    getDocs,
    getDoc,
    updateDoc,
    deleteDoc,
    limit,
    documentId,
    orderBy,
    writeBatch,
    DocumentData,
    QueryDocumentSnapshot,
} from 'firebase/firestore'
import { collectionRef, docRef, fillDoc } from '~common/utils/firestore-helpers'
import type { VendorUpdates } from './types'
import type { InvoiceUpdateParams } from '~/features/invoice-vendor-matching/services/types'
import { Email, EmailAnalysisStatus } from '~common/storage/email'
import { onSnapshot, query as fsQuery } from 'firebase/firestore'
import { StorageModuleDeps } from '~common/storage/types'

export interface EmailInvoiceStorageDeps extends StorageModuleDeps {}

export class EmailInvoiceStorageModule {
    private readonly EMAIL_BUFFER_SIZE = 20

    constructor(private readonly deps: EmailInvoiceStorageDeps) {}

    private ensureAuthenticated(): string {
        const userId = this.deps.getCurrentUserId()
        if (!userId) {
            throw new Error(
                'User must be authenticated to perform this operation',
            )
        }
        return userId
    }

    async getRecentEmails(params: {
        limitCount: number
        hasInvoicesOnly?: boolean
    }): Promise<Email[]> {
        const userId = this.ensureAuthenticated()
        const emailsRef = collectionRef<Email>(
            this.deps.db,
            'users',
            userId,
            'emails',
        )

        let emails: Email[]

        if (!params.hasInvoicesOnly) {
            let q = query(
                emailsRef,
                orderBy('createdAt', 'desc'),
                limit(params.limitCount),
            )

            const querySnapshot = await getDocs(q)
            emails = querySnapshot.docs.map(fillDoc)
        } else {
            // First query: full-success invoices
            let q = query(
                emailsRef,
                orderBy('createdAt', 'desc'),
                where('analysisStatus', '==', 'full-success'),
                where('invoiceClassification', '==', 'invoice'),
                limit(params.limitCount),
            )

            let querySnapshot = await getDocs(q)
            emails = querySnapshot.docs.map(fillDoc)

            if (emails.length < params.limitCount) {
                const remaining = params.limitCount - emails.length

                // Second query: crude-success invoices to fill up
                let q2 = query(
                    emailsRef,
                    orderBy('createdAt', 'desc'),
                    where('analysisStatus', '==', 'crude-success'),
                    where('invoiceClassification', '==', 'invoice'),
                    limit(remaining),
                )

                const querySnapshot2 = await getDocs(q2)
                const additionalEmails = querySnapshot2.docs.map(fillDoc)

                // Combine and sort by createdAt descending
                const allEmails = [...emails, ...additionalEmails]
                allEmails.sort((a, b) => b.createdAt - a.createdAt)

                // Deduplicate by id
                const seenIds = new Set<string>()
                emails = allEmails
                    .filter((email) => {
                        if (seenIds.has(email.id)) {
                            return false
                        }
                        seenIds.add(email.id)
                        return true
                    })
                    .slice(0, params.limitCount)
            }
        }

        return emails
    }

    async getPendingCrudeInvoiceEmails(params: {
        limitCount: number
        order?: 'asc' | 'desc'
    }): Promise<Email[]> {
        const userId = this.ensureAuthenticated()
        const emailsRef = collectionRef<Email>(
            this.deps.db,
            'users',
            userId,
            'emails',
        )

        let qBase = query(
            emailsRef,
            where('analysisStatus', '==', 'crude-success'),
            where('invoiceClassification', '==', 'invoice'),
            orderBy('createdAt', params.order ?? 'asc'),
            limit(params.limitCount),
        )

        const querySnapshot = await getDocs(qBase)
        return querySnapshot.docs.map(fillDoc)
    }

    async storeParsedEmail(params: {
        email: Partial<Email> | null
        now?: number
    }): Promise<StorageModuleCreateResult | undefined> {
        if (!params.email) {
            return undefined
        }
        const now = params.now ?? Date.now()
        const userId = this.ensureAuthenticated()
        const id = this.deps.createDocumentId('emails')
        const emailData: Partial<Email> = {
            externalId: params.email.externalId,
            subject: params.email.subject,
            accountEmail: params.email.accountEmail,
            fromName: params.email.fromName,
            fromEmail: params.email.fromEmail,
            receivedDate: params.email.receivedDate,
            snippet: params.email.snippet,
            originalAttachments: params.email.originalAttachments,
            analysisStatus: params.email.analysisStatus,
            invoiceClassification: params.email.invoiceClassification,
            invoiceClassificationConfidence:
                params.email.invoiceClassificationConfidence,
            createdAt: now,
            updatedAt: now,
        }

        await setDoc(
            docRef(this.deps.db, 'users', userId, 'emails', id),
            emailData,
        )
        return { id }
    }

    async _storeParsedEmail(params: {
        email: ProcessEmailCrudeReturn['email']
        now?: number
    }): Promise<Email> {
        try {
            const now = params.now ?? Date.now()
            const userId = this.ensureAuthenticated()
            if (!userId) {
                throw new Error(
                    'User must be authenticated to perform this operation',
                )
            }
            const id = this.deps.createDocumentId('emails')

            if (!params.email) {
                throw new Error('Email data is required')
            }

            // Exclude raw content fields from storage; we only persist metadata
            const { htmlContent: _, ...emailDataWithoutContent } = params.email

            const emailData: Email = {
                ...(emailDataWithoutContent as Omit<
                    Email,
                    'id' | 'createdAt' | 'updatedAt' | 'htmlContent'
                >),
                id: id,
                createdAt: now,
                updatedAt: now,
            }

            // Store email metadata only (no HTML/text content)
            await setDoc(
                docRef(this.deps.db, 'users', userId, 'emails', id),
                emailData,
            )

            return emailData
        } catch (error) {
            console.error('Error storing parsed email:', error)
            throw new Error(
                `Critical error: Failed to store parsed email - ${
                    error instanceof Error ? error.message : String(error)
                }`,
            )
        }
    }

    async storeParsedEmails(params: {
        emails: ProcessEmailCrudeReturn['email'][]
        now?: number
    }): Promise<Email[]> {
        try {
            const now = params.now ?? Date.now()
            const userId = this.ensureAuthenticated()
            const maxBatchSize = this.EMAIL_BUFFER_SIZE
            const batches: ProcessEmailCrudeReturn['email'][][] = []
            for (let i = 0; i < params.emails.length; i += maxBatchSize) {
                batches.push(params.emails.slice(i, i + maxBatchSize))
            }

            const emailsSaved: Email[] = []

            for (const batchItem of batches) {
                const batch = writeBatch(this.deps.db)
                for (const email of batchItem) {
                    if (!email) {
                        throw new Error('Email data is required')
                    }

                    // Exclude raw content fields from storage; only persist metadata
                    const { htmlContent: _hc, ...emailDataWithoutContent } =
                        email

                    const id = this.deps.createDocumentId('emails')
                    const emailDataToStore: Email = {
                        ...(emailDataWithoutContent as Omit<
                            Email,
                            'id' | 'createdAt' | 'updatedAt' | 'htmlContent'
                        >),
                        id: id,
                        createdAt: now,
                        updatedAt: now,
                    }
                    emailsSaved.push(emailDataToStore)
                    batch.set(
                        docRef(this.deps.db, 'users', userId, 'emails', id),
                        emailDataToStore,
                    )

                    // Skip collecting/storing raw content
                }

                await batch.commit()
            }

            // No-op: raw contents are not persisted

            return emailsSaved
        } catch (error) {
            throw new Error(
                `Critical error: Failed to store parsed emails - ${error instanceof Error ? error.message : String(error)}`,
            )
        }
    }

    async deleteEmail(emailId: string): Promise<void> {
        const userId = this.ensureAuthenticated()
        const emailRef = docRef(
            this.deps.db,
            'users',
            userId,
            'emails',
            emailId,
        )
        await deleteDoc(emailRef)
    }

    async updateParsedEmail(params: {
        emailId: string
        analysisStatus: EmailAnalysisStatus
        invoiceClassification?: InvoiceClassificationResult
        now?: number
    }): Promise<void> {
        const now = params.now ?? Date.now()
        const userId = this.ensureAuthenticated()
        const emailRef = docRef(
            this.deps.db,
            'users',
            userId,
            'emails',
            params.emailId,
        )
        await updateDoc(emailRef, {
            ...(params.invoiceClassification && {
                invoiceClassification:
                    params.invoiceClassification.classification,
                invoiceClassificationConfidence:
                    params.invoiceClassification.confidence,
            }),
            analysisStatus: params.analysisStatus,
            updatedAt: now,
        })
    }

    async storeInvoiceData(params: {
        metadata: ParsedInvoiceMetadata
        fileId: string
        emailId?: string
        vendorId?: string
        vendorSuggestions: VendorSuggestion[]
        now?: number
    }): Promise<StorageModuleCreateResult> {
        const now = params.now ?? Date.now()
        const userId = this.ensureAuthenticated()

        const id = this.deps.createDocumentId('documents')
        const documentData: Omit<Invoice, 'id'> = {
            sourceEmailId: params.emailId ?? null,
            vendorId: params.vendorId ?? null,
            vendorSuggestions: params.vendorSuggestions,
            sourceType: params.emailId ? 'email' : 'upload',
            fileType: 'pdf',
            googleDriveFileId: params.fileId,
            metadata: params.metadata,
            vendorLinkConfirmed: false,
            createdAt: now,
            updatedAt: now,
        }

        await setDoc(
            docRef(this.deps.db, 'users', userId, 'documents', id),
            documentData,
        )
        return { id }
    }

    async updateExtractedInvoiceData(params: {
        invoiceId: string
        metadata: Partial<InvoiceDataExtractionResult>
        now?: number
    }): Promise<void> {
        const now = params.now ?? Date.now()
        const userId = this.ensureAuthenticated()
        const invoiceRef = docRef(
            this.deps.db,
            'users',
            userId,
            'documents',
            params.invoiceId,
        )

        const docSnap = await getDoc(invoiceRef)
        if (!docSnap.exists()) {
            throw new Error(`Invoice with id ${params.invoiceId} not found`)
        }
        const currentInvoice = docSnap.data() as Invoice
        const currentMetadata = currentInvoice.metadata

        await updateDoc(invoiceRef, {
            metadata: {
                ...currentMetadata,
                ...params.metadata,
                reason: currentMetadata.reason,
            },
            updatedAt: now,
        })

        if (!currentInvoice.vendorId) {
            return
        }

        // If invoice has an associated vendor, update related vendor fields
        const vendorUpdates: VendorUpdates = {}

        const updateSingleValueField = (
            metadataField: string | null | undefined,
            currentMetadataField: string | null | undefined,
            removeKey: keyof Pick<
                VendorUpdates,
                'aliasesToRemove' | 'addressesToRemove'
            >,
            addKey: keyof Pick<
                VendorUpdates,
                'aliasesToAdd' | 'addressesToAdd'
            >,
        ) => {
            if (metadataField === undefined) {
                return
            }
            const oldValue = currentMetadataField
            const newValue = metadataField

            if (oldValue && oldValue !== newValue) {
                vendorUpdates[removeKey] = [oldValue]
            }
            if (newValue) {
                vendorUpdates[addKey] = [newValue]
            }
        }

        const updateArrayField = (
            metadataField: string[] | undefined,
            currentMetadataField: string[] | undefined,
            removeKey: keyof Pick<
                VendorUpdates,
                'ibansToRemove' | 'emailAddressesToRemove'
            >,
            addKey: keyof Pick<
                VendorUpdates,
                'ibansToAdd' | 'emailAddressesToAdd'
            >,
        ) => {
            if (metadataField === undefined) {
                return
            }
            const oldArray = currentMetadataField || []
            const newArray = metadataField

            const itemsToRemove = oldArray?.filter(
                (item) => !newArray?.includes(item),
            )
            const itemsToAdd = newArray?.filter(
                (item) => !oldArray?.includes(item),
            )

            if (itemsToRemove.length > 0) {
                vendorUpdates[removeKey] = itemsToRemove
            }
            if (itemsToAdd.length > 0) {
                vendorUpdates[addKey] = itemsToAdd
            }
        }

        updateSingleValueField(
            params.metadata.vendorName,
            currentMetadata.vendorName,
            'aliasesToRemove',
            'aliasesToAdd',
        )

        updateSingleValueField(
            params.metadata.vendorAddress,
            currentMetadata.vendorAddress,
            'addressesToRemove',
            'addressesToAdd',
        )

        updateArrayField(
            params.metadata.vendorIbans,
            currentMetadata.vendorIbans,
            'ibansToRemove',
            'ibansToAdd',
        )

        updateArrayField(
            params.metadata.vendorEmailAddresses,
            currentMetadata.vendorEmailAddresses,
            'emailAddressesToRemove',
            'emailAddressesToAdd',
        )

        if (Object.keys(vendorUpdates).length > 0) {
            await this.updateVendor({
                id: currentInvoice.vendorId,
                data: vendorUpdates,
                now,
            })
        }
    }

    async updateInvoice(params: InvoiceUpdateParams): Promise<void> {
        const now = params.now ?? Date.now()
        const userId = this.ensureAuthenticated()

        const updateData: Partial<Invoice> = {
            updatedAt: now,
        }

        // Only include fields that are explicitly provided
        if (params.vendorSuggestions !== undefined) {
            updateData.vendorSuggestions = params.vendorSuggestions
        }
        if (params.vendorId !== undefined) {
            updateData.vendorId = params.vendorId
        }
        if (params.vendorLinkConfirmed !== undefined) {
            updateData.vendorLinkConfirmed = params.vendorLinkConfirmed
        }

        const invoiceRef = docRef(
            this.deps.db,
            'users',
            userId,
            'documents',
            params.invoiceId,
        )

        const docSnap = await getDoc(invoiceRef)
        if (!docSnap.exists()) {
            throw new Error(`Invoice with id ${params.invoiceId} not found`)
        }

        // If no updates apart from the timestamp, skip the update
        if (Object.keys(updateData).length === 1) {
            return
        }

        await updateDoc(invoiceRef, updateData)
    }

    async storeVendorData(
        params: Pick<
            Vendor,
            | 'name'
            | 'ibans'
            | 'emailAddresses'
            | 'addresses'
            | 'country'
            | 'aliases'
        > & {
            now?: number
        },
    ): Promise<StorageModuleCreateResult> {
        const now = params.now ?? Date.now()
        const userId = this.ensureAuthenticated()
        const aliases = [...params.aliases]

        if (!aliases?.includes(params.name)) {
            aliases.push(params.name)
        }

        const id = this.deps.createDocumentId('vendors')
        const vendorData: Omit<Vendor, 'id'> = {
            name: params.name,
            ibans: params.ibans,
            emailAddresses: params.emailAddresses,
            addresses: params.addresses,
            country: params.country,
            aliases,
            createdAt: now,
            updatedAt: now,
        }

        await setDoc(
            docRef(this.deps.db, 'users', userId, 'vendors', id),
            vendorData,
        )
        return { id }
    }

    async getUnprocessedInvoiceEmails(): Promise<Email[]> {
        const userId = this.ensureAuthenticated()
        const emailsRef = collectionRef<Email>(
            this.deps.db,
            'users',
            userId,
            'emails',
        )
        const q = query(
            emailsRef,
            where('analysisStatus', '==', 'crude-success'),
            where('invoiceClassification', '==', 'invoice'),
        )
        const querySnapshot = await getDocs(q)
        return querySnapshot.docs.map(fillDoc)
    }

    async getEmailsByExternalIds(
        externalIds: string[],
    ): Promise<Record<string, Email>> {
        if (externalIds.length === 0) {
            return {}
        }

        const userId = this.ensureAuthenticated()
        const emailsRef = collectionRef<Email>(
            this.deps.db,
            'users',
            userId,
            'emails',
        )

        // Firestore limits "in" queries to 30 items, so we batch them
        const BATCH_SIZE = 30
        const batches: string[][] = []
        for (let i = 0; i < externalIds.length; i += BATCH_SIZE) {
            batches.push(externalIds.slice(i, i + BATCH_SIZE))
        }

        const batchPromises = batches.map(async (batch) => {
            const q = query(emailsRef, where('externalId', 'in', batch))
            const querySnapshot = await getDocs(q)
            return querySnapshot.docs.map(fillDoc)
        })

        const batchResults = await Promise.all(batchPromises)
        const emails = batchResults.flat()

        return fromPairs(emails.map((email) => [email.externalId, email]))
    }

    async getDocumentIdsByEmailIds(
        emailIds: string[],
    ): Promise<Record<string, string[]>> {
        if (emailIds.length === 0) {
            return {}
        }

        const userId = this.ensureAuthenticated()
        const documentsRef = collectionRef<Invoice>(
            this.deps.db,
            'users',
            userId,
            'documents',
        )

        // Firestore limits "in" queries to 30 items, so we batch them
        const BATCH_SIZE = 30
        const batches: string[][] = []
        for (let i = 0; i < emailIds.length; i += BATCH_SIZE) {
            batches.push(emailIds.slice(i, i + BATCH_SIZE))
        }

        const batchPromises = batches.map(async (batch) => {
            const q = query(documentsRef, where('sourceEmailId', 'in', batch))
            const querySnapshot = await getDocs(q)
            return querySnapshot.docs.map(fillDoc)
        })

        const batchResults = await Promise.all(batchPromises)
        const documents = batchResults.flat()

        return fromPairsMulti(
            documents.map((document) => [document.sourceEmailId!, document.id]),
        )
    }

    async getAllInvoices(): Promise<Invoice[]> {
        const userId = this.ensureAuthenticated()
        const documentsRef = collectionRef<Invoice>(
            this.deps.db,
            'users',
            userId,
            'documents',
        )
        const q = query(documentsRef)
        const querySnapshot = await getDocs(q)
        return querySnapshot.docs.map(fillDoc)
    }

    /**
     * Subscribes to realtime changes in the current user's invoices (documents collection).
     * Emits batched added/modified/removed items. Returns an unsubscribe function.
     * If startAtTimestamp is provided, only processes items with createdAt >= timestamp on the initial add batch.
     */
    subscribeToInvoices(
        handler: (changes: {
            added: Invoice[]
            modified: Invoice[]
            removed: string[]
        }) => void,
        options?: { startAtTimestamp?: number },
    ): () => void {
        const userId = this.deps.getCurrentUserId()
        if (!userId) {
            return () => {}
        }
        const documentsRef = collectionRef<Invoice>(
            this.deps.db,
            'users',
            userId,
            'documents',
        )

        const q = fsQuery(documentsRef)
        const initialCutoff = options?.startAtTimestamp ?? null
        let isFirstSnapshot = true

        return onSnapshot(q, (snapshot) => {
            const added: Invoice[] = []
            const modified: Invoice[] = []
            const removed: string[] = []

            for (const change of snapshot.docChanges()) {
                if (change.type === 'added') {
                    if (change.doc.metadata.hasPendingWrites) continue
                    const data = change.doc.data()
                    const item = { ...data, id: change.doc.id } as Invoice
                    // If start cutoff specified, skip existing docs on first snapshot
                    if (
                        isFirstSnapshot &&
                        initialCutoff != null &&
                        typeof item.createdAt === 'number' &&
                        item.createdAt < initialCutoff
                    ) {
                        continue
                    }
                    added.push(item)
                } else if (change.type === 'modified') {
                    if (change.doc.metadata.hasPendingWrites) continue
                    const data = change.doc.data()
                    modified.push({ ...data, id: change.doc.id } as Invoice)
                } else if (change.type === 'removed') {
                    removed.push(change.doc.id)
                }
            }

            isFirstSnapshot = false
            if (added.length || modified.length || removed.length) {
                handler({ added, modified, removed })
            }
        })
    }

    async getDocumentById(documentId: string): Promise<Invoice | null> {
        const userId = this.ensureAuthenticated()
        const documentRef = docRef(
            this.deps.db,
            'users',
            userId,
            'documents',
            documentId,
        )

        try {
            const docSnap = await getDoc(documentRef)
            if (docSnap.exists()) {
                const data = docSnap.data()
                if (data) {
                    return {
                        ...data,
                        id: docSnap.id,
                    } as Invoice
                }
            }
            return null
        } catch (error) {
            console.error('Error fetching document:', error)
            return null
        }
    }

    async getDocumentsByEmailId(emailId: string): Promise<Invoice[]> {
        const userId = this.ensureAuthenticated()
        const documentsRef = collectionRef<Invoice>(
            this.deps.db,
            'users',
            userId,
            'documents',
        )
        const q = query(documentsRef, where('sourceEmailId', '==', emailId))
        const querySnapshot = await getDocs(q)
        return querySnapshot.docs.map(fillDoc)
    }

    async getEmailById(id: string): Promise<Email | null> {
        const userId = this.ensureAuthenticated()
        const emailRef = docRef<Email>(
            this.deps.db,
            'users',
            userId,
            'emails',
            id,
        )
        const docSnap = await getDoc(emailRef)
        if (docSnap.exists()) {
            return fillDoc(docSnap)
        }
        return null
    }

    async getEmailsByIds(ids: string[]): Promise<Record<string, Email>> {
        if (ids.length === 0) {
            return {}
        }

        const userId = this.ensureAuthenticated()
        const emailsRef = collectionRef<Email>(
            this.deps.db,
            'users',
            userId,
            'emails',
        )

        // Firestore limits "in" queries to 30 items, so we batch them
        const BATCH_SIZE = 30
        const batches: string[][] = []
        for (let i = 0; i < ids.length; i += BATCH_SIZE) {
            batches.push(ids.slice(i, i + BATCH_SIZE))
        }

        const batchPromises = batches.map(async (batch) => {
            const q = query(emailsRef, where(documentId(), 'in', batch))
            const querySnapshot = await getDocs(q)
            return querySnapshot.docs.map(fillDoc)
        })

        const batchResults = await Promise.all(batchPromises)
        const emails = batchResults.flat()

        return fromPairs(emails.map((email) => [email.id, email]))
    }

    async getInvoicesByIds(ids: string[]): Promise<Record<string, Invoice>> {
        if (ids.length === 0) {
            return {}
        }

        const userId = this.ensureAuthenticated()
        const documentsRef = collectionRef<Invoice>(
            this.deps.db,
            'users',
            userId,
            'documents',
        )

        // Firestore limits "in" queries to 30 items, so we batch them
        const BATCH_SIZE = 30
        const batches: string[][] = []
        for (let i = 0; i < ids.length; i += BATCH_SIZE) {
            batches.push(ids.slice(i, i + BATCH_SIZE))
        }

        const batchPromises = batches.map(async (batch) => {
            const q = query(documentsRef, where(documentId(), 'in', batch))
            const querySnapshot = await getDocs(q)
            return querySnapshot.docs.map(fillDoc)
        })

        const batchResults = await Promise.all(batchPromises)
        const invoices = batchResults.flat()

        return fromPairs(invoices.map((invoice) => [invoice.id, invoice]))
    }

    async updateVendor(params: {
        id: Vendor['id']
        data: VendorUpdates
        now?: number
    }): Promise<void> {
        const userId = this.ensureAuthenticated()
        const vendorRef = docRef(
            this.deps.db,
            'users',
            userId,
            'vendors',
            params.id,
        )

        const docSnap = await getDoc(vendorRef)
        if (!docSnap.exists()) {
            throw new Error(`Vendor with id ${params.id} not found`)
        }
        const currentVendor = docSnap.data() as Vendor

        const updateData: Partial<Vendor> = {
            updatedAt: params.now ?? Date.now(),
        }

        // Single-value fields: direct overwrite
        if (params.data.name !== undefined) {
            updateData.name = params.data.name
        }
        if (params.data.country !== undefined) {
            updateData.country = params.data.country
        }

        // Array fields: handle replace, add, and remove operations
        const updateArrayField = (
            fieldName: keyof Pick<
                Vendor,
                'ibans' | 'emailAddresses' | 'addresses' | 'aliases'
            >,
            replace?: string[],
            add?: string[],
            remove?: string[],
        ) => {
            let currentArray = currentVendor[fieldName]

            if (replace !== undefined) {
                currentArray = replace
            } else {
                if (add && add.length > 0) {
                    currentArray = [...new Set([...currentArray, ...add])]
                }
                if (remove && remove.length > 0) {
                    const removeSet = new Set(remove)
                    currentArray = currentArray.filter(
                        (item) => !removeSet.has(item),
                    )
                }
            }

            if (
                replace !== undefined ||
                (add && add.length > 0) ||
                (remove && remove.length > 0)
            ) {
                updateData[fieldName] = currentArray
            }
        }

        updateArrayField(
            'ibans',
            params.data.ibans,
            params.data.ibansToAdd,
            params.data.ibansToRemove,
        )
        updateArrayField(
            'emailAddresses',
            params.data.emailAddresses,
            params.data.emailAddressesToAdd,
            params.data.emailAddressesToRemove,
        )
        updateArrayField(
            'addresses',
            params.data.addresses,
            params.data.addressesToAdd,
            params.data.addressesToRemove,
        )
        updateArrayField(
            'aliases',
            params.data.aliases,
            params.data.aliasesToAdd,
            params.data.aliasesToRemove,
        )

        // If nothing to update, apart from the timestamp, skip the update
        if (Object.keys(updateData).length === 1) {
            return
        }

        await updateDoc(vendorRef, updateData)
    }

    /**
     * Gets a vendor by its ID.
     */
    async getVendorById(vendorId: string): Promise<Vendor | null> {
        const userId = this.ensureAuthenticated()
        const vendorRef = docRef(
            this.deps.db,
            'users',
            userId,
            'vendors',
            vendorId,
        )

        const docSnap = await getDoc(vendorRef)
        if (docSnap.exists()) {
            const data = docSnap.data()
            if (data) {
                return {
                    ...data,
                    id: docSnap.id,
                } as Vendor
            }
        }
        return null
    }
}
